import React, { useState, useCallback } from "react";
import { useSaveHeadroomFinalDataMutation } from "graphql/types/graphql";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { enqueueSnackbar } from "notistack";
import { HeadroomFinalResponse } from "graphql/types/graphql";

interface SaveResult {
  loading: boolean;
  error: string | null;
}

interface SaveHeadroomFinalResults {
  saveFinalData: (increasedPenetration: number, edges: HeadroomFinalResponse[]) => Promise<void>;
  finalDataResult: SaveResult;
}

export const useHeadroomFinalSaveData = (): SaveHeadroomFinalResults => {
  const { selectedProject, segmentGroups } = useHeadroomContext();
  const [saveFinalDataMutation, { loading: finalDataLoading, error: finalDataMutationError }] =
    useSaveHeadroomFinalDataMutation();
  const { showError, clearError } = useErrorSnackbar();
  const [finalDataErrorState, setFinalDataError] = useState<string | null>(null);

  const dataSegmentGroup = segmentGroups?.find((group) => group.method === "DATA");
  const manualSegmentGroup = segmentGroups?.find((group) => group.method === "MANUAL");
  const dataSegmentType = dataSegmentGroup?.type || "Unknown Data Segment";
  const manualSegmentType = manualSegmentGroup?.type || "Unknown Manual Segment";

  const saveFinalData = useCallback(
    async (increasedPenetration: number, edges: HeadroomFinalResponse[]) => {
      if (!selectedProject) {
        setFinalDataError("Project name is required to save final data.");
        showError("Project name is required to save final data.");
        return;
      }
      if (!edges?.length) {
        setFinalDataError("No final data available to save.");
        showError("No final data available to save.");
        return;
      }

      try {
        const input = {
          projectName: selectedProject,
          increasedPenetration,
          edges: edges.map((edge) => ({
            segment: dataSegmentType,
            segmentValue: edge.segmentValue,
            segment2Name: manualSegmentType,
            segment2: edge.segmentRange || edge.segmentValue,
            totalCustomers: edge.totalCustomers || 0,
            totalProspects: edge.totalProspects || 0,
            penetration: edge.penetration || 0,
            driverInitial: edge.driverInitial || null,
            driverFinal: edge.driverFinal || null,
            headroom: edge.headroom || null,
            totalCustomersAfter: edge.totalCustomersAfter || 0,
            totalDriverInitial: edge.totalDriverInitial || null,
            totalDriverFinal: edge.totalDriverFinal || null,
            totalHeadroom: edge.totalHeadroom || null,
            headroomPercent: edge.headroomPercent || null,
          })),
        };
        const result = await saveFinalDataMutation({
          variables: { input },
        });
        if (!result.data?.saveHeadroomFinalData.success) {
          const errorMessage = result.data?.saveHeadroomFinalData.errorMessage || "Failed to save final data.";
          setFinalDataError(errorMessage);
          showError(errorMessage);
          return;
        }
        setFinalDataError(null);
        clearError();
        enqueueSnackbar(
          result.data?.saveHeadroomFinalData.isUpdate
            ? "Final data updated successfully!"
            : "Final data saved successfully!",
          { variant: "success" }
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setFinalDataError(errorMessage);
        showError(errorMessage);
      }
    },
    [selectedProject, dataSegmentType, manualSegmentType, showError, saveFinalDataMutation, clearError]
  );

  return {
    saveFinalData,
    finalDataResult: {
      loading: finalDataLoading,
      error: finalDataErrorState || finalDataMutationError?.message || null,
    },
  };
};
